import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "../../components/settings/confirm-dialog";
import { EditableList, type ListItem } from "../../components/settings/editable-list";
import { useSubsoilOptions } from "../../hooks/use-api";
import { useCreateSubsoilOption, useDeleteSubsoilOption, useUpdateSubsoilOption } from "../../hooks/use-settings";

export function SubsoilOptionsPage(): React.ReactElement {
  const { data, isLoading, error } = useSubsoilOptions();
  const createMutation = useCreateSubsoilOption();
  const updateMutation = useUpdateSubsoilOption();
  const deleteMutation = useDeleteSubsoilOption();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const items: ListItem[] = (data?.items ?? []).map((item) => ({ ...item, id: String(item.id) }));

  const handleSave = (item: Partial<ListItem>): void => {
    if (item.id) {
      updateMutation.mutate(
        { id: Number(item.id), name: item.name, isActive: item.isActive },
        {
          onSuccess: () => toast.success("Subsoil option updated"),
          onError: () => toast.error("Failed to update subsoil option"),
        },
      );
    } else {
      createMutation.mutate(
        { name: item.name ?? "" },
        {
          onSuccess: () => toast.success("Subsoil option created"),
          onError: () => toast.error("Failed to create subsoil option"),
        },
      );
    }
  };

  const handleConfirmDelete = (): void => {
    if (!pendingDeleteId) return;
    deleteMutation.mutate(Number(pendingDeleteId), {
      onSuccess: () => toast.success("Subsoil option deactivated"),
      onError: () => toast.error("Failed to deactivate subsoil option"),
    });
    setPendingDeleteId(null);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-1">Subsoil Options</h2>
      <p className="text-sm text-muted-foreground mb-2">
        Manage subsoil classification options. Inactive options are hidden from new records but preserved on existing
        ones.
      </p>
      {isLoading ? (
        <p className="text-sm text-muted-foreground" data-testid="subsoil-options-loading">
          Loading…
        </p>
      ) : error ? (
        <p className="text-sm text-destructive" data-testid="subsoil-options-error">
          Failed to load subsoil options.
        </p>
      ) : (
        <EditableList
          items={items}
          entityName="Subsoil Option"
          onSave={handleSave}
          onReorder={() => {}}
          onDelete={setPendingDeleteId}
        />
      )}
      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Deactivate Subsoil Option"
        message="This subsoil option will be hidden from new records but preserved on existing ones. Continue?"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
}
